import { Component, Mixins, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import '@gig/magic-ui-kit';
import '@gig/magic-pages';
import '@gig/magic-responsible-gaming-prompters';
import { magic } from '@gig/magic-sdk';
import type { IMessageEmit } from '@gig/magic-messaging';
import { notificationService } from '@gig/magic-messaging';
import type { IDepositDataEvent, ILoyaltyPointsUpdateEvent } from '@gig/magic-sdk/dist/events';
import { SessionCode } from '@gig/magic-sdk/dist/events';
import {
    configQuery,
    CssDevice,
    debounce,
    FeatureEnabledV2,
    FeatureLocationV2,
    getFeatureEnabled,
    getFeatureValueV2,
    getTrackingService,
    ipCountryQuery,
    languageQuery,
    MagicConfigMixin,
    modalService,
    nativeAppService,
    queryTranslations,
    SessionChange,
    updateInterpolationParams,
} from '@gig/magic-common';
import '@gig/magic-gamification/dist/components/loyalty-progress';
import '@gig/magic-agco/dist/components/subdivision-modal';
import '../content-toggle';

import { MainNavigation } from '../main-navigation';
import { TopNavigation } from '../top-navigation';
import { BottomNavigation } from '../bottom-navigation';
import { AppFooter } from '../footer';
import { ModalContainer } from '../modal-container';
import { RegilyManager } from '../regily-manager';
import { TermsUpdatedManager } from '../terms-updated-manager';
import { SourceOfWealthManager } from '../source-of-wealth-manager';
import { GameFrame } from '../game-frame';
import { ContinuePlaying } from '../continue-playing';
import { BlockManager } from '../block-manager';
import { NotFound } from '../not-found';
import { SavageWidget } from '../savage-widget';
import {
    LOYALTY_NAMESPACE,
    actions as loyaltyActions,
    getters as loyaltyGetters,
} from '../../store/modules/loyalty';
import type { OmitFirst } from '../../types';
import {
    AuthRestriction,
    config,
    intercom,
    liveChat,
    setHtmlLang,
    smarticoService,
    zendeskChat,
} from '../../services';
import {
    BRITE_AUTH_SESSION_KEY,
    getModals,
    getTimeOutHandling,
    isModalOpen,
    RETURNING_USER_KEY,
    TimeOutHandling,
} from '../../util';

import { RouteName } from '../../routes/routes.constants';
import { prerender } from '../../services/seo/prerender.service';
import { syncOpenGraphMetaTags } from '../../services/seo/open-graph.service';
import {
    actions as rgActions,
    RESPONSIBLE_GAMING_NAMESPACE,
} from '../../store/modules/responsible-gaming';
import { responsibleGamingModalService } from '../responsible-gaming-modal/responsible-gaming-modal-service';

import '../../styles/default.scss';
import { JurisdictionMixin } from '../../mixins/jurisdiction.mixin';
import { NotFoundMixin } from '../../mixins/notfound.mixin';
import {
    ACCOUNT_REQUIRES_VERIFICATION_UPDATED,
    BREAKPOINT_DEVICE_UPDATED,
    BREAKPOINT_FULLSCREEN_UPDATED,
    DEVICE_QUERY_IS_IOS,
    DEVICE_QUERY_IS_IPAD,
    DEVICE_QUERY_IS_MOBILE,
    ENABLE_CONTINUE_PLAYING_UPDATED,
    Feature,
    GAMES_CURRENT_GAME_SLUG_UPDATED,
    GAMES_JACKPOTS_LIST,
    GAMES_LIST,
    MESSAGING_MARK_READ,
    NET_POSITION_BAR_UPDATED,
    PANIC_BAR_VISIBLE,
    RECONNECTING_UPDATED,
    SINGLE_LANGUAGE_QUERY,
    USER_FIRST_LOG_IN_UPDATED,
} from '@gig/magic-messages';
import { Country } from '@gig/magic-sdk/dist/adapter/users/getCountries';
import { IUser } from '@gig/magic-sdk/dist/adapter/users/user';
import { calculateLoyaltyTier } from './app.utils';
import { alternateLink } from '@gig/magic-frontend-toolkit/dist/services/alternate-link';
import { linktag } from '@gig/magic-frontend-toolkit/dist/services/linktag';
import { ModalPayPlay } from '../modal-pay-play';
import { BANK_ID_STEPS } from '@gig/magic-account/dist/components/bank-id/sign-up/sign-up.constants';

const rgModule = namespace(RESPONSIBLE_GAMING_NAMESPACE);

// Safety valve for redirectAfterFirstLogin's retry loop, in case the
// configured URL can never actually be reached - not meant to bound normal
// sequential compliance flows (subdivision info, verification, etc.), which
// is why it's generous.
const MAX_FIRST_LOGIN_REDIRECT_ATTEMPTS = 20;
let firstLoginRedirectAttempts = 0;

const loyaltyModule = namespace(LOYALTY_NAMESPACE);

@Component({
    components: {
        MainNavigation,
        TopNavigation,
        BottomNavigation,
        AppFooter,
        ModalContainer,
        ModalPayPlay,
        TermsUpdatedManager,
        SourceOfWealthManager,
        GameFrame,
        ContinuePlaying,
        BlockManager,
        RegilyManager,
        NotFound,
        SavageWidget,
    },
})
export default class App extends Mixins(
    MagicConfigMixin,
    JurisdictionMixin,
    NotFoundMixin,
) {
    @FeatureEnabledV2({
        slug: Feature.COOKIE_POPUP,
    })
    cookiePopupEnabled = false;

    /**
     * is top navigation enabled
     */
    @FeatureEnabledV2({
        slug: Feature.TOP_NAVIGATION,
    })
    isTopNavigationEnabled = true;

    @FeatureLocationV2({
        slug: "modal-pay-and-play",
        default: [],
    })
    modalPayAndPlayLocation: string[] = [];

    country: Country | null = null;

    isLoggedIn = false;
    
    payPlayEnabled: boolean = false;
    payPlayRange!: {min: number; max: number};

    reconnecting: boolean = false;
    private reconnectingTimeout: number | null = null;
    private readonly RECONNECTING_TIMEOUT_MS = 5000;

    @rgModule.Action('clearLimits')
    clearLimits!: OmitFirst<typeof rgActions.clearLimits>;

    @rgModule.Action('loadUserBlocks')
    loadUserBlocks!: OmitFirst<typeof rgActions.loadUserBlocks>;

    @rgModule.Action('clearTimeoutState')
    clearTimeoutState!: OmitFirst<typeof rgActions.clearTimeoutState>;

    @loyaltyModule.Action('fetchUserProgress')
    fetchUserProgress!: OmitFirst<typeof loyaltyActions.fetchUserProgress>;

    @loyaltyModule.Action('setLoyaltyTier')
    setLoyaltyTier!: OmitFirst<typeof loyaltyActions.setLoyaltyTier>;

    @loyaltyModule.Action('updateUserProgress')
    updateUserProgress!: OmitFirst<typeof loyaltyActions.updateUserProgress>;

    @loyaltyModule.Getter('progress')
    loyaltyProgress!: ReturnType<typeof loyaltyGetters.progress>;

    @loyaltyModule.Getter('points')
    loyaltyPoints!: ReturnType<typeof loyaltyGetters.points>;

    @loyaltyModule.Getter('level')
    loyaltyLevel!: ReturnType<typeof loyaltyGetters.level>;

    @loyaltyModule.Getter('tier')
    loyaltyTier!: ReturnType<typeof loyaltyGetters.tier>;

    @loyaltyModule.Getter('loyaltyLoaded')
    loyaltyLoaded!: ReturnType<typeof loyaltyGetters.loyaltyLoaded>;

    showNetPositionBar = false;
    enableContinuePlaying = false;
    isMobile = false;
    isFullscreen: boolean = false;
    shouldShowPWAInstall: boolean = false;
    currentGameSlug: string | null = null;
    panicBarVisible = false;
    isWalletOpen: boolean = false;

    private subscriptions: Array<() => void> = [];
    private firstLoginRedirectPending = false;
    private waitingForNavigationBeforeFirstLoginRedirect = false;

    get isMobileGamePlayView() {
        return !!this.currentGameSlug && this.isMobile;
    }

    get isBingoLobbyMobile() {
        return this.$route.name === RouteName.BINGO_LOBBY && this.isMobile;
    }

    get shouldDisableRGAlerts() {
        return this.$route.matched.some((route) => {
            return route.meta.shouldDisableRGAlerts === true;
        });
    }

    get shouldHideMainMenu() {
        const isLandingPage = this.$route.name === RouteName.LANDING;

        const hasHideMainMenuMeta = this.$route.matched.some((route) => {
            return route.meta.shouldHideMainMenu === true;
        });

        return hasHideMainMenuMeta || (!this.isMobile && isLandingPage);
    }

    get isNotFoundRoute(): boolean {
        return this.isNotFound;
    }

    get isSportsbookPage(): boolean {
        return this.$route.name === RouteName.SPORTSBOOK;
    }

    mounted() {
        const attributes = config.cookieBot;
        if (!attributes?.src) {
            return;
        }

        const cookieBot = document.createElement('script');
        for (const [key, value] of Object.entries(attributes)) {
            cookieBot.setAttribute(key, value as any);
        }
        document.head.appendChild(cookieBot);
    }

    async created() {
        this.country = await ipCountryQuery();

        if (!window.inTestEnvironment) {
            prerender.waitFor(this.requestGames(), this.requestJackpots());
        }

        this.setDefaultMetaTags();
        linktag.setLanguagesConfig(config.languages);

        if (magic.session.isCreated()) {
            this.handleSessionCreated();
        }
        magic.events.sessionDestroyed(() => this.handleSessionDestroyed());
        magic.events.sessionCreated(() => this.handleSessionCreated());
        magic.events.sessionChange(this.handleSessionChanged);
        magic.events.firstDeposit(this.handleFirstDeposit);
        magic.events.subsequentDeposit(this.handleSubsequentDeposit);

        liveChat?.updateLiveChat(magic.session.getUser());
        intercom?.load();

        const language = await languageQuery();

        smarticoService.setUserData(magic.session.getUser(), language);

        this.checkIsWalletOpen();

        this.getPWAstatus();

        this.subscribeToModalPayAndPlay();

        window.eventBus.subscribe<boolean>(
            RECONNECTING_UPDATED,
            (e) => {
                this.reconnecting = e.payload!;
                this.handleReconnectingStateChange(e.payload!);
            },
            { replay: true },
        );

        // Handle page visibility changes to fix mobile reconnection issues
        this.setupVisibilityChangeHandler();

        window.eventBus.subscribe<boolean>(
            NET_POSITION_BAR_UPDATED,
            (event) => {
                this.showNetPositionBar = event.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<boolean>(
            ENABLE_CONTINUE_PLAYING_UPDATED,
            (event) => {
                this.enableContinuePlaying = event.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<CssDevice>(
            BREAKPOINT_DEVICE_UPDATED,
            async () => {
                this.isMobile = await window.queryBus.query(
                    DEVICE_QUERY_IS_MOBILE,
                )!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<boolean>(
            BREAKPOINT_FULLSCREEN_UPDATED,
            (e) => {
                this.isFullscreen = e.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<string | null>(
            GAMES_CURRENT_GAME_SLUG_UPDATED,
            (e) => {
                this.currentGameSlug = e.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<boolean>(
            PANIC_BAR_VISIBLE,
            async (e) => {
                this.panicBarVisible = e.payload!;
            },
            { replay: true },
        );

        // Defer until country and feature-location config are initialized by component setup.
        setTimeout(() => {
            this.payPlayEnabled = this.modalPayAndPlayLocation.includes(this.country!) ? true : false;
        })

        window.eventBus.subscribe<boolean>(USER_FIRST_LOG_IN_UPDATED, (e) => {
            if (!e.payload) {
                return;
            }

            this.firstLoginRedirectPending = true;
            this.waitForNavigationThenTryFirstLoginRedirect();
        });

        // Blocking modals (subdivision info, mandatory messages, identity
        // verification, etc.) don't always trigger a router navigation when
        // they close (some are injected straight into the DOM), so watch
        // modalService directly to retry once nothing is open anymore.
        modalService.onModalsChange((modals) => {
            if (modals.length === 0) {
                this.waitForNavigationThenTryFirstLoginRedirect();
            }
        });

        // A blocking modal can close while the user is still pending
        // verification (eg. failed a scan and was sent to upload documents
        // manually) - retry once that requirement actually clears.
        window.eventBus.subscribe<boolean>(
            ACCOUNT_REQUIRES_VERIFICATION_UPDATED,
            (e) => {
                if (!e.payload) {
                    this.waitForNavigationThenTryFirstLoginRedirect();
                }
            },
        );
    }

    @Watch('loyaltyLoaded')
    onLoyaltyLoaded() {
        const user = magic.session.getUser();
        if (this.loyaltyLoaded) {
            this.setLoyaltyInterpolationParams(
                this.loyaltyProgress || 0,
                this.loyaltyLevel || 0,
                this.loyaltyPoints || 0,
                user
            );
        }
    }

    @Watch('magicConfig.language')
    async languageChange() {
        setHtmlLang(this.magicConfig.language);
        syncOpenGraphMetaTags();

        setHtmlLang(this.magicConfig.language);
        syncOpenGraphMetaTags();

        if (config?.zendesk?.version === 'legacy') {
            await zendeskChat?.setZendeskSettings?.(this.magicConfig.language);
            await zendeskChat?.setLanguage(this.magicConfig.language);
        }
    }

    @Watch('isBingoLobbyMobile')
    onBingoLobbyMobileChange( isBingoLobbyMobile: boolean ) {
        document.body.style.overflow = isBingoLobbyMobile ? 'hidden' : '';
    }

    private requestJackpots(): Promise<void> {
        return new Promise((resolve) => {
            const subscription = window.eventBus.subscribe(
                GAMES_JACKPOTS_LIST,
                () => {
                    resolve();
                    subscription.unsubscribe();
                },
                { replay: true },
            );
        });
    }

    @Watch('$route.path')
    onRouteChange() {
        this.setDefaultMetaTags();
        this.resetNotFound();
    }

    get isTopNavigationLight() {
        return this.$route.matched.reduce(
            (prev, cur) => ({ ...prev, ...cur.meta.topNavigation }),
            {
                isLight: false,
            },
        ).isLight;
    }

    private setDefaultMetaTags() {
        queryTranslations().then(() => {
            linktag.setCanonicalTagFromPath(this.$route.path);
            alternateLink.setAlternateTag(this.$route.path, {
                useBaseUrl: true,
            });
            syncOpenGraphMetaTags();
            syncOpenGraphMetaTags();
        });
    }

    @Watch('$route.query')
    checkIsWalletOpen() {
        const queryModalPath = this.$route.query.modal;
        this.isWalletOpen =
            (Array.isArray(queryModalPath) &&
                queryModalPath.some((path) =>
                    path?.includes(RouteName.WALLET),
                )) ||
            !!queryModalPath?.includes(RouteName.WALLET);
        this.resetNotFound();
    }

    @SessionChange({ immediate: true })
    async onSessionChange(isUserLoggedIn: boolean) {
        this.isLoggedIn = isUserLoggedIn;
    }

    onAcceptedCookiePopup() {
        getTrackingService().trackCookieAcceptance();
    }

    markMessageAsRead(payload: IMessageEmit) {
        window.commandBus.publish(MESSAGING_MARK_READ, payload);
    }

    async getPWAstatus() {
        const [isIOS, isIpad] = await Promise.all([
            window.queryBus.query<boolean>(DEVICE_QUERY_IS_IOS),
            window.queryBus.query<boolean>(DEVICE_QUERY_IS_IPAD),
        ]);
        this.shouldShowPWAInstall = isIOS! || isIpad!;
    }

    private requestGames(): Promise<void> {
        return new Promise((resolve) => {
            const subscription = window.eventBus.subscribe(
                GAMES_LIST,
                () => {
                    resolve();
                    subscription.unsubscribe();
                },
                { replay: true },
            );
        });
    }

    private async handleSessionDestroyed() {
        liveChat?.updateLiveChat(null);
        intercom?.shutdown();
        this.clearLimits();
        responsibleGamingModalService.close();

        await this.clearTimeoutState();
        this.subscriptions.forEach((unsub) => unsub());
        this.subscriptions = [];

        this.checkRouteRestriction(); 
    }

    private handleSessionChanged(code: SessionCode) {
        if (
            [
                SessionCode.EXPIRED,
                SessionCode.TERMINATED_BY_ANOTHER_LOGIN,
                SessionCode.TERMINATED_BY_PRESET_TIME,
                SessionCode.TERMINATED_BY_SELF_EXCLUSION,
                SessionCode.TERMINATED_BY_GEO_LOCATION,
            ].includes(code)
        ) {
            nativeAppService.postSystemLogout();
        }

        if (code === SessionCode.TERMINATED_BY_GEO_LOCATION) {
            notificationService.error(this.$t('session.logout.geo-location'));
        }

        if (SessionCode.LOGGED_OFF) {
            nativeAppService.postLogout();
        }
    }

    private handleFirstDeposit(event: IDepositDataEvent) {
        const { amount, currency } = event;
        nativeAppService.trackFirstDeposit(amount, currency);
    }

    private handleSubsequentDeposit(event: IDepositDataEvent) {
        const { amount, currency } = event;
        nativeAppService.trackDeposit(amount, currency);
    }


    // Triggered right when the user's first login is confirmed - fires the
    // redirect immediately (after letting the triggering navigation settle,
    // to avoid racing with it) rather than waiting for other modals or
    // pending verification to clear. Those open on top of whatever page we
    // land on instead of blocking us from navigating there in the first
    // place. modalService/ACCOUNT_REQUIRES_VERIFICATION_UPDATED still retry
    // this as a fallback in case some other guard intercepted our push and
    // redirected elsewhere before we could arrive.
    private async tryFirstLoginRedirect() {
        if (!this.firstLoginRedirectPending) {
            return;
        }

        const enabled = await getFeatureEnabled(
            Feature.REDIRECTION_AFTER_FIRST_LOGIN,
        );
        if (!enabled) {
            this.firstLoginRedirectPending = false;
            return;
        }

        const configuredPath = await getFeatureValueV2<string>(
            Feature.REDIRECTION_AFTER_FIRST_LOGIN,
            '',
        );
        if (!configuredPath) {
            this.firstLoginRedirectPending = false;
            return;
        }

        const singleLanguage = await window.queryBus.query<boolean>(
            SINGLE_LANGUAGE_QUERY,
        );
        const redirectPath = singleLanguage
            ? configuredPath
            : `/${await languageQuery()}${configuredPath}`;

        if (this.hasArrivedAtFirstLoginRedirect(redirectPath)) {
            this.firstLoginRedirectPending = false;
            return;
        }

        firstLoginRedirectAttempts += 1;
        if (firstLoginRedirectAttempts > MAX_FIRST_LOGIN_REDIRECT_ATTEMPTS) {
            this.firstLoginRedirectPending = false;
            return;
        }

        this.$router.push({ path: redirectPath }).catch(() => {
            // Something else superseded our push (eg. a navigation that was
            // already in flight) - wait for the dust to settle and retry.
            this.waitForNavigationThenTryFirstLoginRedirect();
        });
    }

    // Ensures we only ever check/act once the router isn't itself mid-
    // transition, no matter which signal (login event, modal closing,
    // verification clearing, a superseded push) asked us to try. Checking
    // `router.currentRoute` while another navigation is still resolving
    // would race with it - this waits for whatever's in flight to confirm
    // first. Debounced so multiple signals firing close together don't
    // register duplicate one-off hooks.
    private waitForNavigationThenTryFirstLoginRedirect() {
        if (this.waitingForNavigationBeforeFirstLoginRedirect) {
            return;
        }

        this.waitingForNavigationBeforeFirstLoginRedirect = true;
        const unregister = this.$router.afterEach(() => {
            this.waitingForNavigationBeforeFirstLoginRedirect = false;
            unregister();
            this.tryFirstLoginRedirect();
        });
    }

    private hasArrivedAtFirstLoginRedirect(redirectPath: string): boolean {
        const target = this.$router.resolve(redirectPath).route;
        const current = this.$router.currentRoute;

        if (!target.name) {
            return current.fullPath === redirectPath;
        }

        return (
            current.name === target.name ||
            isModalOpen(target.name as RouteName, current.query, this.$router)
        );
    }

    private checkRouteRestriction() {
        const lastRestriction = this.$route.matched.reduce(
            (prev, { meta }) =>
                meta.authRestriction !== undefined
                    ? meta.authRestriction
                    : prev,
            AuthRestriction.UNRESTRICTED
        );
        if (lastRestriction === AuthRestriction.AUTH_REQUIRED) {
            this.$router.replace({
                name: RouteName.LOGIN,
                query: {
                    backTo: this.$route.fullPath,
                },
            });
        }
    }

    /**
     * Loads the Time-out state of a restored session and terminates it in the
     * jurisdictions where being logged in during a Time-out is not permitted.
     */
    private async resolveTimeOutBlock() {
        const timeOutHandling = await getTimeOutHandling();

        if (timeOutHandling === TimeOutHandling.NONE) {
            return;
        }

        const isTimedOut = await this.loadUserBlocks();

        if (!isTimedOut || timeOutHandling !== TimeOutHandling.LOGIN_BLOCKED) {
            return;
        }

        notificationService.error(this.$t('login.login-failed.blocked'), {
            uniqueID: 'time-out-login-blocked',
        });

        try {
            await magic.methods.users.logout();
        } catch (error) {
            console.error('Error logging out a timed out user:', error);
        }
    }

    private async handleSessionCreated() {
        const user = magic.session.getUser();
        const language = await languageQuery();
        await this.$nextTick();

        if (!this.$route.name) {
            await new Promise<void>((resolve) => {
                this.$router.onReady(resolve);
            });
        }

        const isBriteStepOpen = getModals(
            this.$route.query,
            this.$router,
        ).some(
            (route) =>
                route.name === RouteName.SIGN_UP_HYBRID &&
                route.params.step === BANK_ID_STEPS.STEP_BRITE,
        );
        const profile = user ? await magic.methods.users.getProfile() : null;
        let isBriteAuth = false;

        try {
            isBriteAuth =
                window.localStorage.getItem(BRITE_AUTH_SESSION_KEY) === 'true';
        } catch {
            isBriteAuth = false;
        }

        if (isBriteAuth && user && !profile?.mobile && !isBriteStepOpen) {
            await this.$router.push({
                name: RouteName.SIGN_UP_HYBRID,
                params: {
                    step: BANK_ID_STEPS.STEP_BRITE,
                },
            });
        }

        liveChat?.updateLiveChat(user);
        intercom?.update(user);
        smarticoService.setUserData(magic.session.getUser(), language);

        if (user) {
            await this.resolveTimeOutBlock();
        }

        if (user) {
            await this.resolveTimeOutBlock();
        }

        this.subscriptions.push(
            magic.events.sowqRequested(() => {
                if (this.$route.name !== RouteName.SOW) {
                    this.$router.push({
                        name: RouteName.SOW,
                    });
                }
            }).unsub
        );

        this.subscriptions.push(
            magic.events.loyaltyPointsUpdate((data) =>
                this.debouncedLoyaltyPointsUpdate(data)
            ).unsub
        );

        this.fetchUserProgress({ userId: user!.userId });

        const loyaltyPromise: ReturnType<
            typeof magic.methods.loyalty.userProgress
        > = magic.invokeCached(
            60,
            magic.methods.loyalty,
            'userProgress',
        )({
            userId: user!.userId,
        });
        const loyaltyData = await loyaltyPromise;

        this.setLoyaltyInterpolationParams(
            loyaltyData.progress,
            loyaltyData.level,
            loyaltyData.points,
            user,
        );

        try {
            window.localStorage.setItem(RETURNING_USER_KEY, '1');
        } catch (e) {
            console.error(e);
        }
    }

    /**
    * As magic warned, there are cases in which multiple messages are sent
    * almost at the same time. We debounce them, just in case
    */
    private debouncedLoyaltyPointsUpdate = debounce(
        this.handleLoyaltyPointsUpdate,
        300,
        true
    );

    private handleLoyaltyPointsUpdate(data: ILoyaltyPointsUpdateEvent) {
        const user = magic.session.getUser();
        if (!user) {
            return;
        }
        const loyaltyTierIndex = calculateLoyaltyTier(
            data.level,
            config.loyalty?.tiers,
            user
        );
        this.updateUserProgress({ updatedProgress: data });
        this.setLoyaltyTier({ tier: loyaltyTierIndex });
    }

    private setLoyaltyInterpolationParams(
        progress: number,
        level: number,
        points: number,
        user: IUser | null
    ) {
        if (!user) {
            return;
        }
        const loyaltyTierIndex = calculateLoyaltyTier(
            level,
            config.loyalty?.tiers,
            user
        );
        this.setLoyaltyTier({ tier: loyaltyTierIndex });
        const loyaltyTier = this.$t(`loyalty.tiers.tier-${loyaltyTierIndex}`);
        updateInterpolationParams({
            loyaltyLevel: level.toString(),
            loyaltyBalance: points.toString(),
            loyaltyProgress: Math.round(progress).toString(),
            loyaltyTier,
        });
    }

    private async subscribeToModalPayAndPlay() {
        const payAndPlay =
                    (await configQuery<{enabled: boolean; minDeposit: number; maxDeposit: number}>(
                        `modalPayAndPlay`,
                    )) ?? { enabled: false, minDeposit: 0, maxDeposit: 0 };

        this.payPlayRange = { min: payAndPlay.minDeposit, max: payAndPlay.maxDeposit }
    }

    /**
     * Handles reconnecting state changes with timeout failsafe
     * @param isReconnecting - Whether the app is currently reconnecting
     */
    private handleReconnectingStateChange(isReconnecting: boolean) {
        // Clear any existing timeout
        if (this.reconnectingTimeout !== null) {
            window.clearTimeout(this.reconnectingTimeout);
            this.reconnectingTimeout = null;
        }

        this.reconnecting = isReconnecting;

        // If reconnecting, set a timeout to force it off after max duration
        // This prevents the loader from getting stuck indefinitely on mobile
        if (isReconnecting) {
            this.reconnectingTimeout = window.setTimeout(() => {
                console.warn(
                    '[App] Reconnecting timeout reached, forcing reconnecting state to false',
                );
                this.reconnecting = false;
                this.reconnectingTimeout = null;
            }, this.RECONNECTING_TIMEOUT_MS);
        }
    }

    /**
     * On mobile, when the browser is backgrounded, connections are suspended.
     * When returning, we need to ensure the reconnection state is properly resolved.
     */
    private setupVisibilityChangeHandler() {
        if (typeof document.hidden === 'undefined') {
            return;
        }

        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) {
                // If we're stuck in reconnecting state, give it a moment to resolve
                if (this.reconnecting) {
                    // Give the SDK 5 seconds to properly emit reconnection success
                    // If it hasn't resolved by then, force it
                    window.setTimeout(() => {
                        if (this.reconnecting) {
                            this.reconnecting = false;
                            if (this.reconnectingTimeout !== null) {
                                window.clearTimeout(this.reconnectingTimeout);
                                this.reconnectingTimeout = null;
                            }
                        }
                    }, 5000);
                }
            }
        });
    }
}
